import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, ScanText, X } from "lucide-react";
import { openPdfDoc, renderPageToCanvas } from "./pdfSetup";
import { fetchTiffPage } from "./api";
import { isTiff } from "./theme";
import { getPageState, pageKey } from "./pageState";
import { INPUT, MONO } from "./theme";
import { downloadMarkedSheets } from "./markedExport";

/** Lazily rendered sheet card: thumbnail + takeoff overlay + search marks
 * + editable sheet name + selection checkbox. */
function SheetThumb({ drawing, page, docProxy, tif, docState, active, selected, onToggleSelect, onOpen, boxes, onRename }) {
  const ref = useRef(null);
  const [thumb, setThumb] = useState(null); // {w, h}
  const canvasRef = useRef(null);
  const ps = getPageState(docState, drawing.drawing_id, page);
  const pageItems = (docState.items || []).filter((i) => i.fileId === drawing.drawing_id && i.page === page);

  useEffect(() => {
    const el = ref.current;
    if (!el || (!docProxy && !tif)) return undefined;
    let alive = true;
    const io = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting || thumb) return;
      io.disconnect();
      try {
        if (tif) {
          // TIFF page: server-rendered rendition straight into the canvas
          const url = await fetchTiffPage(drawing.drawing_id, page, 720);
          const im = new Image();
          await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
          if (!alive) return;
          setThumb({ w: im.naturalWidth, h: im.naturalHeight });
          if (canvasRef.current) {
            canvasRef.current.width = im.naturalWidth;
            canvasRef.current.height = im.naturalHeight;
            canvasRef.current.getContext("2d").drawImage(im, 0, 0);
          }
          URL.revokeObjectURL(url);
          return;
        }
        // render at CSS display size x devicePixelRatio (v2) — Retina-sharp
        // thumbnails instead of a stretched low-res raster. The old 360 px
        // render is v1; this cache key changes so old thumbs regenerate.
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const { canvas, width, height } = await renderPageToCanvas(docProxy, page, ps.rotation || 0, Math.round(360 * dpr));
        if (!alive) return;
        setThumb({ w: width, h: height });
        if (canvasRef.current) {
          canvasRef.current.width = width;
          canvasRef.current.height = height;
          canvasRef.current.getContext("2d").drawImage(canvas, 0, 0);
        }
      } catch { /* thumbnail failed — leave placeholder */ }
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
  }, [docProxy, page, ps.rotation, tif]); // eslint-disable-line

  const unitH = thumb ? (1000 * thumb.h) / thumb.w : 1414;
  return (
    <div ref={ref} className={`relative rounded-xl border-2 bg-white p-1.5 shadow-sm transition-colors ${active ? "border-[#f97316]" : selected ? "border-blue-400" : "border-slate-200"}`} data-testid={`sheet-card-${page}`}>
      <span className="absolute left-2.5 top-2.5 z-10 rounded bg-[#0b172a]/85 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">{page}</span>
      <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select sheet ${page}`}
        className="absolute right-2.5 top-2.5 z-10 h-4 w-4 accent-[#f97316]" data-testid={`sheet-select-${page}`} />
      {pageItems.length > 0 && (
        <span className="absolute bottom-9 left-2.5 z-10 rounded bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white" title={`${pageItems.length} takeoffs`}>A · {pageItems.length}</span>
      )}
      <button onClick={onOpen} className="block w-full" aria-label={`Open sheet ${page}`} data-testid={`sheet-open-${page}`}>
        <div className="relative w-full overflow-hidden rounded-lg bg-slate-100" style={{ aspectRatio: thumb ? `${thumb.w}/${thumb.h}` : "1000/1414" }}>
          <canvas ref={canvasRef} className="h-full w-full" draggable={false} />
          {thumb && (
            <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 1000 ${unitH}`} preserveAspectRatio="none">
              {(boxes || []).map((b, i) => (
                <rect key={`s${i}`} x={b.x} y={b.y} width={b.w} height={b.h} fill="rgba(250,204,21,0.4)" />
              ))}
              {(ps.highlights || []).map((h) => (
                <rect key={h.id} x={h.x} y={h.y} width={h.w} height={h.h} fill="rgba(250,204,21,0.3)" />
              ))}
              {pageItems.map((it) => {
                const pts = (it.points || []).map((p) => `${p.x},${p.y}`).join(" ");
                if (it.type === "count") {
                  const [p] = it.points;
                  return <circle key={it.id} cx={p.x} cy={p.y} r={8} fill={it.color} />;
                }
                if (it.type === "area") return <polygon key={it.id} points={pts} fill={it.color} fillOpacity={0.25} stroke={it.color} strokeWidth={6} vectorEffect="non-scaling-stroke" />;
                return <polyline key={it.id} points={pts} fill="none" stroke={it.color} strokeWidth={6} vectorEffect="non-scaling-stroke" strokeLinecap="round" />;
              })}
            </svg>
          )}
          {!thumb && <div className="absolute inset-0 animate-pulse bg-slate-200/60" />}
        </div>
      </button>
      <input value={ps.name} onChange={(e) => onRename(page, e.target.value)}
        aria-label={`Sheet name for page ${page}`}
        className="mt-1 w-full rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-center text-[11px] font-medium text-slate-700 hover:border-slate-200 focus:border-[#f97316] focus:outline-none"
        data-testid={`sheet-name-${page}`} />
      {boxes?.length > 0 && <p className={`${MONO} text-center text-[9px] text-amber-600`}>{boxes.length} match{boxes.length > 1 ? "es" : ""}</p>}
    </div>
  );
}

/**
 * Full sheet gallery for the open PDF: search (name / page / page text with
 * background indexing), auto name sheets, selection, marked-PDF download.
 */
export default function SheetGallery({ drawing, docState, currentPage, onOpenPage, onClose, onAutoName, onRename, pageText, mutate }) {
  const [q, setQ] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [docProxy, setDocProxy] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [matches, setMatches] = useState(null); // Map<page, boxes[]>
  const [progress, setProgress] = useState("");
  const [markBusy, setMarkBusy] = useState("");
  const searchSeq = useRef(0);
  const tif = isTiff(drawing);

  useEffect(() => {
    let alive = true;
    if (tif) {
      // TIFF: pages are image renditions — no pdf.js document at all
      setNumPages(drawing.pages || 1);
      setDocProxy(null);
      return undefined;
    }
    (async () => {
      try {
        const d = await openPdfDoc(drawing.drawing_id);
        if (alive) { setDocProxy(d); setNumPages(d.numPages); }
      } catch { toast.error("Could not open this PDF"); onClose(); }
    })();
    return () => { alive = false; };
  }, [drawing.drawing_id]); // eslint-disable-line

  // background text search (debounced, cancellable)
  useEffect(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || (!docProxy && !tif)) { setMatches(null); setProgress(""); return undefined; }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      const found = new Map();
      for (let pg = 1; pg <= (tif ? numPages : docProxy.numPages); pg++) {
        if (searchSeq.current !== seq) return;
        setProgress(`Searching page ${pg} of ${tif ? numPages : docProxy.numPages}…`);
        try {
          let boxes = [];
          if (!tif) {
            const { spans, width, height } = await pageText(drawing.drawing_id, pg);
            const uh = (1000 * height) / width;
            boxes = spans
              .filter((s) => s.str.toLowerCase().includes(needle))
              .map((s) => ({ x: (s.x / width) * 1000, y: (s.y / height) * uh, w: (s.w / width) * 1000, h: (s.h / height) * uh }));
          }
          const sheet = (getPageState(docState, drawing.drawing_id, pg).name || "").toLowerCase();
          if (boxes.length || sheet.includes(needle) || String(pg) === needle) found.set(pg, boxes);
        } catch { /* page text failed — skip */ }
      }
      if (searchSeq.current === seq) { setMatches(found); setProgress(""); }
    }, 400);
    return () => clearTimeout(timer);
  }, [q, docProxy, tif, numPages, drawing.drawing_id, docState, pageText]);

  const pages = useMemo(() => {
    const all = Array.from({ length: numPages }, (_, i) => i + 1);
    return matches ? all.filter((p) => matches.has(p)) : all;
  }, [numPages, matches]);

  const downloadMarked = async () => {
    if (!sel.size) return;
    try {
      await downloadMarkedSheets(drawing, [...sel], docState, {
        searchBoxesByPage: matches ? Object.fromEntries(matches) : {},
        onProgress: (i, n) => setMarkBusy(`Preparing page ${i + 1} of ${n}…`),
      });
    } catch { toast.error("Marked PDF export failed"); }
    finally { setMarkBusy(""); }
  };

  // contained inside the takeoff workspace (absolute, not fixed) so the
  // module header/job context stay visible; Escape closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#0b172a]/97 backdrop-blur-sm" data-testid="sheet-gallery">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#24324a] px-4 py-3">
        <p className="text-sm font-bold text-white">{drawing.name}</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tif ? "Search sheet number or name (no text layer on TIFF)" : "Search sheet number or any words on the page"}
          title={tif ? "TIFF pages are images — sheet text isn't searchable, but names and page numbers are" : undefined}
          className="tk-dark-input h-9 w-72 rounded-lg border border-white/20 bg-white/10 px-3 text-sm text-white placeholder:text-white/40 focus:border-[#f97316] focus:outline-none"
          data-testid="gallery-search" />
        {progress && <span className="text-[11px] text-white/50" data-testid="gallery-search-progress">{progress}</span>}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onAutoName} className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:bg-white/10" data-testid="gallery-autoname">
            <ScanText className="h-3.5 w-3.5" /> Auto name sheets
          </button>
          <button onClick={() => setSel(new Set(matches ? [...matches.keys()] : pages))} className="rounded-lg border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:bg-white/10" data-testid="gallery-select-all">
            {matches ? `Select results (${matches.size})` : "Select all"}
          </button>
          <button onClick={() => { setSel(new Set()); setQ(""); }} className="rounded-lg border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:bg-white/10" data-testid="gallery-clear">Clear</button>
          <button onClick={downloadMarked} disabled={!sel.size} className="inline-flex items-center gap-1.5 rounded-lg bg-[#f97316] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-[#fb923c] disabled:opacity-40" data-testid="gallery-marked-download">
            <Download className="h-3.5 w-3.5" /> Download marked PDF ({sel.size})
          </button>
          <button onClick={onClose} aria-label="Close gallery" className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white" data-testid="gallery-close"><X className="h-5 w-5" /></button>
        </div>
      </div>
      {markBusy && <p className="bg-[#f97316]/15 px-4 py-1.5 text-center text-[11px] font-semibold text-[#fb923c]" data-testid="gallery-mark-progress">{markBusy}</p>}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {matches && <p className="mb-3 text-xs text-white/60" data-testid="gallery-match-count">{matches.size} sheet{matches.size === 1 ? "" : "s"} match “{q.trim()}”</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {pages.map((pg) => (
            <SheetThumb key={pg} drawing={drawing} page={pg} docProxy={docProxy} tif={tif} docState={docState}
              active={pg === currentPage}
              selected={sel.has(pg)}
              onToggleSelect={() => setSel((s) => { const n = new Set(s); n.has(pg) ? n.delete(pg) : n.add(pg); return n; })}
              onOpen={() => { onOpenPage(pg); onClose(); }}
              boxes={matches?.get(pg)}
              onRename={onRename} />
          ))}
        </div>
        {!pages.length && <p className="py-16 text-center text-sm text-white/40">No sheets match.</p>}
      </div>
    </div>
  );
}
